import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Pages
import Home from './pages/Home';
import Concept from './pages/Concept';
import BarMobile from './pages/BarMobile';
import BarDetail from './pages/BarDetail';
import StudioCrea from './pages/StudioCrea';
import PrestationCocktail from './pages/PrestationCocktail';
import LocationPartenaires from './pages/LocationPartenaires';
import Contact from './pages/Contact';
import Projet from './pages/Projet';
import AuthCallback from './pages/AuthCallback';
import AdminDashboard from './pages/admin/Dashboard';
import AdminConcepts from './pages/admin/Concepts';
import AdminContacts from './pages/admin/Contacts';
import AdminSettings from './pages/admin/Settings';
import AdminPages from './pages/admin/Pages';
import AdminPortfolio from './pages/admin/Portfolio';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Contact Info - used across the site
export const CONTACT_INFO = {
  address: "1 rue des Ternes",
  city: "75017 Paris",
  phone: "01 86 70 06 16",
  email: "contact@studiobar.com"
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Settings Context
const SettingsContext = createContext(null);

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  return (
    <SettingsContext.Provider value={{ settings, setSettings, loading }}>
      {children}
    </SettingsContext.Provider>
  );
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      {!isAdminRoute && <Header />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/concept" element={<Concept />} />
        <Route path="/bar-mobile" element={<BarMobile />} />
        <Route path="/bar-mobile/:id" element={<BarDetail />} />
        <Route path="/studio-crea" element={<StudioCrea />} />
        <Route path="/prestation-cocktail" element={<PrestationCocktail />} />
        <Route path="/location-partenaires" element={<LocationPartenaires />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/projet" element={<Projet />} />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminLayout><AdminDashboard /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/concepts" element={
          <ProtectedRoute>
            <AdminLayout><AdminConcepts /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/contacts" element={
          <ProtectedRoute>
            <AdminLayout><AdminContacts /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute>
            <AdminLayout><AdminSettings /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/pages" element={
          <ProtectedRoute>
            <AdminLayout><AdminPages /></AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/portfolio" element={
          <ProtectedRoute>
            <AdminLayout><AdminPortfolio /></AdminLayout>
          </ProtectedRoute>
        } />
      </Routes>
      {!isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SettingsProvider>
          <div className="min-h-screen bg-black">
            <AppRouter />
          </div>
        </SettingsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
